import { AttendancePage } from './pages/AttendancePage';
import { FeaturesPage } from './pages/FeaturesPage';
import { BrandingProvider } from './branding';
import { BrandingPage } from './pages/BrandingPage';
import { AnnouncementPage } from './pages/AnnouncementPage';
import { useEffect, useState } from 'react';
import type { Calendar } from '../shared/contracts';
import { api, type ClientSession } from './api';
import { CookieNotice } from './components/CookieNotice';
import { Shell } from './components/Shell';
import { ErrorMessage, Loading } from './components/Status';
import { CalendarPage } from './pages/CalendarPage';
import { AccountPage } from './pages/AccountPage';
import { EventPage } from './pages/EventPage';
import { LoginPage } from './pages/LoginPage';
import { SubscriptionPage } from './pages/SubscriptionPage';
import { SettingsPage } from './pages/SettingsPage';
import { InvitationPage } from './pages/InvitationPage';
import { ImportPage } from './pages/ImportPage';
import { AdminPage } from './pages/AdminPage';
import { CalendarMembersPage } from './pages/CalendarMembersPage';
import { ParticipationPage } from './pages/ParticipationPage';
import { ParticipationManagePage } from './pages/ParticipationManagePage';

import { useMobileViewport } from './use-mobile-viewport';
import { legalDocument } from '../shared/legal';
import { LegalPage } from './pages/LegalPage';

export function App() {
  useMobileViewport();
  const [session, setSession] = useState<ClientSession | null>(null);
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cookieDetails, setCookieDetails] = useState(false);
  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [sessionResult, calendarResult] = await Promise.all([api.session(), api.calendars()]);
      setSession(sessionResult);
      setCalendars(calendarResult.calendars);
      if (sessionResult.user) {
        const { preferences } = await api.preferences();
        const view = {
          month: 'dayGridMonth',
          week: 'timeGridWeek',
          day: 'timeGridDay',
          list: 'listMonth',
        }[preferences.view];
        localStorage.setItem('dragonfly-calendar-view', view);
        localStorage.setItem('dragonfly-calendar-ids', preferences.calendarIds.join(','));
        document.documentElement.dataset.theme =
          preferences.theme === 'system' ? '' : preferences.theme;
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The calendar could not load');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!legalDocument(window.location.pathname)) void load();
  }, []);

  const path = window.location.pathname;
  const policy = legalDocument(path);
  let content: React.ReactNode;
  if (policy) content = <LegalPage document={policy} />;
  else if (loading)
    content = (
      <main id="main-content" className="center-page">
        <Loading />
      </main>
    );
  else if (error)
    content = (
      <main id="main-content" className="center-page">
        <ErrorMessage message={error} retry={() => void load()} />
      </main>
    );
  else if (path === '/login') content = <LoginPage session={session} />;
  else if (path === '/invite') content = <InvitationPage session={session} />;
  else if (path === '/account') content = <AccountPage session={session} calendars={calendars} />;
  else if (path === '/participation/new')
    content = <ParticipationManagePage session={session} calendars={calendars} />;
  else if (/^\/participation\/[^/]+\/manage$/.test(path))
    content = (
      <ParticipationManagePage
        session={session}
        calendars={calendars}
        formId={path.split('/')[2]!}
      />
    );
  else if (path === '/participation') content = <ParticipationPage session={session} />;
  else if (/^\/(polls|forms)\/\d{4}\/[a-z0-9-]+\/?$/.test(path))
    content = <ParticipationPage session={session} path={path} />;
  else if (/^\/events\/\d{4}\/\d{1,2}\/[a-z0-9-]+(?:\/on\/[^/]+)?\/?$/.test(path))
    content = <EventPage path={path} session={session} />;
  else if (path.startsWith('/subscriptions/'))
    content = (
      <SubscriptionPage
        id={path.split('/').filter(Boolean)[1] || ''}
        calendars={calendars}
        session={session}
      />
    );
  else if (path === '/import') content = <ImportPage session={session} calendars={calendars} />;
  else if (path === '/admin/participation')
    content = <AttendancePage session={session} calendars={calendars} />;
  else if (/^\/admin\/participation\/events\/[^/]+$/.test(path))
    content = (
      <AttendancePage session={session} calendars={calendars} eventId={path.split('/')[4]!} />
    );
  else if (path === '/admin/features') content = <FeaturesPage session={session} />;
  else if (path === '/admin/branding') content = <BrandingPage session={session} />;
  else if (path === '/admin/announcements') content = <AnnouncementPage session={session} />;
  else if (/^\/admin\/calendars\/[^/]+\/members\/?$/.test(path)) {
    const calendarId = decodeURIComponent(path.split('/')[3] || '');
    content = <CalendarMembersPage session={session} calendarId={calendarId} />;
  } else if (path === '/admin') content = <AdminPage session={session} />;
  else if (path === '/settings')
    content = (
      <SettingsPage session={session} calendars={calendars} onCalendarsChanged={setCalendars} />
    );
  else content = <CalendarPage calendars={calendars} session={session} />;

  return (
    <BrandingProvider superAdmin={Boolean(session?.capabilities.superAdmin)}>
      <Shell session={session} onCookieInfo={() => setCookieDetails(true)}>
        {content}
        <CookieNotice detailsOpen={cookieDetails} onCloseDetails={() => setCookieDetails(false)} />
      </Shell>
    </BrandingProvider>
  );
}
